'use client';

import BoltIcon from '@mui/icons-material/Bolt';
import { Box, Button, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useT } from '@/hooks';

interface PrivyOtpVerificationProps {
  email?: string;
  otpCode: string;
  setOtpCode: (code: string) => void;
  isAuthenticating: boolean;
  onVerify: () => void;
  onResend: () => void;
}

export const PrivyOtpVerification = ({
  email,
  otpCode,
  setOtpCode,
  isAuthenticating,
  onVerify,
  onResend,
}: PrivyOtpVerificationProps) => {
  const { t } = useT();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        animation: 'fadeIn 0.3s ease-out',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Locked Icon Decoration */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          color: 'primary.main',
        }}
      >
        <BoltIcon sx={{ fontSize: '1.5rem' }} />
      </Box>

      <Typography variant="h6" fontWeight={700} textAlign="center" gutterBottom>
        {t('privyOtp.verifyEmail')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 2.5, maxWidth: 300 }}
      >
        {t('privyOtp.sentCode')} <br />
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {email}
        </Box>
      </Typography>

      <Box sx={{ position: 'relative', width: '100%', maxWidth: 320, mb: 4 }}>
        {/* Real Hidden Input */}
        <input
          autoFocus
          type="tel"
          value={otpCode}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            setOtpCode(val);
          }}
          disabled={isAuthenticating}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            zIndex: 1,
            caretColor: 'transparent',
          }}
        />

        {/* Stylized Boxes */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((index) => {
            const isFocused = otpCode.length === index || (otpCode.length === 6 && index === 5);
            const hasValue = otpCode.length > index;
            const digit = otpCode[index] || '';

            return (
              <Box
                key={index}
                sx={{
                  width: 44,
                  height: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: (theme) =>
                    isFocused
                      ? 'primary.main'
                      : hasValue
                        ? alpha(theme.palette.primary.main, 0.3)
                        : 'divider',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.05)
                      : alpha(theme.palette.common.black, 0.02),
                  transition: 'all 0.2s ease',
                  boxShadow: isFocused
                    ? (theme) => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`
                    : 'none',
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    color: hasValue ? 'text.primary' : 'text.disabled',
                    fontFamily: 'monospace',
                  }}
                >
                  {digit}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={isAuthenticating || otpCode.length < 6}
        onClick={onVerify}
        sx={{
          py: 1.5,
          borderRadius: 3,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.9375rem',
          boxShadow: (theme) => `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
          mb: 2.5,
          '&:hover': {
            boxShadow: (theme) => `0 12px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
          },
        }}
      >
        {isAuthenticating ? t('privyOtp.verifying') : t('privyOtp.verifyAndContinue')}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('privyOtp.didntReceive')}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={onResend}
          disabled={isAuthenticating}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            p: 0,
            minWidth: 'auto',
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {t('privyOtp.resendCode')}
        </Button>
      </Box>
    </Box>
  );
};

export default PrivyOtpVerification;
